import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Phone, ArrowLeft, Shield, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { BusinessAnalytics } from '@/lib/posthog';

interface OTPState {
  phone: string;
  code: string;
  step: 'phone' | 'verify';
  loading: boolean;
  error: string | null;
  resendCooldown: number;
}

export default function PhoneOTPSignIn() {
  const navigate = useNavigate();
  const [otpState, setOtpState] = useState<OTPState>({
    phone: '',
    code: '',
    step: 'phone',
    loading: false,
    error: null,
    resendCooldown: 0
  });

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (otpState.resendCooldown > 0) {
      const timer = setTimeout(() => {
        setOtpState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpState.resendCooldown]);

  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otpState.phone || otpState.phone.replace(/\D/g, '').length !== 10) {
      setOtpState(prev => ({ ...prev, error: 'Please enter a valid 10-digit phone number' }));
      return;
    }

    setOtpState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Format phone number (E.164)
      const formattedPhone = `+1${otpState.phone.replace(/\D/g, '')}`;

      // Send OTP using Supabase-native auth (Twilio configured in Supabase Auth)
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
        options: { channel: 'sms' }
      });

      if (error) {
        throw error;
      }

      setOtpState(prev => ({
        ...prev,
        step: 'verify',
        loading: false,
        resendCooldown: 60
      }));

      toast.success('Verification code sent to your phone');

      // Track OTP sent event (mask phone for analytics)
      const maskedPhone = formattedPhone.replace(/\d(?=\d{4})/g, '*');
      BusinessAnalytics.trackEvent('otp_sent', {
        masked_phone: maskedPhone,
        type: 'signin'
      });
    } catch (error: any) {
      console.error('OTP send error:', error);
      setOtpState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to send verification code. Please try again.'
      }));
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otpState.code || otpState.code.length !== 6) {
      setOtpState(prev => ({ ...prev, error: 'Please enter the 6-digit verification code' }));
      return;
    }

    setOtpState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formattedPhone = `+1${otpState.phone.replace(/\D/g, '')}`;

      // Verify OTP and create session using Supabase-native auth
      const { error } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: otpState.code,
        type: 'sms'
      });

      if (error) {
        throw error;
      }

      toast.success('Successfully signed in!');

      // Track successful OTP verification (mask phone)
      const maskedPhone = formattedPhone.replace(/\d(?=\d{4})/g, '*');
      BusinessAnalytics.trackEvent('otp_verified', {
        masked_phone: maskedPhone,
        method: 'phone_otp'
      });

      // Navigate to dashboard; AuthContext will pick up the session
      navigate('/dashboard');
    } catch (error: any) {
      console.error('OTP verify error:', error);
      setOtpState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Invalid verification code. Please try again.'
      }));
    }
  };

  const handleResendCode = async () => {
    if (otpState.resendCooldown > 0) return;
    
    setOtpState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formattedPhone = `+1${otpState.phone.replace(/\D/g, '')}`;

      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
        options: { channel: 'sms' }
      });

      if (error) {
        throw error;
      }

      setOtpState(prev => ({
        ...prev,
        loading: false,
        resendCooldown: 60
      }));
      
      toast.success('New verification code sent');
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      setOtpState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to resend code. Please try again.'
      }));
    }
  };

  const handleBackToPhone = () => {
    setOtpState(prev => ({
      ...prev,
      step: 'phone',
      code: '',
      error: null
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Phone className="h-8 w-8 text-purple-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              {otpState.step === 'phone' ? 'Sign in with Phone' : 'Verify Your Phone'}
            </CardTitle>
            <p className="text-muted-foreground">
              {otpState.step === 'phone' 
                ? 'Enter your phone number to receive a verification code'
                : `We sent a code to ${formatPhoneNumber(otpState.phone)}`
              }
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {otpState.error && (
              <Alert variant="destructive">
                <AlertDescription>{otpState.error}</AlertDescription>
              </Alert>
            )}

            {otpState.step === 'phone' ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formatPhoneNumber(otpState.phone)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      if (digits.length <= 10) {
                        setOtpState(prev => ({ ...prev, phone: digits }));
                      }
                    }}
                    className="text-lg"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    We'll send a verification code via SMS
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={otpState.loading || otpState.phone.length !== 10}
                >
                  {otpState.loading ? (
                    <>
                      <Clock className="mr-2 h-4 w-4 animate-spin" />
                      Sending Code...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Send Verification Code
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleOTPSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="123456"
                    value={otpState.code}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      if (digits.length <= 6) {
                        setOtpState(prev => ({ ...prev, code: digits }));
                      }
                    }}
                    className="text-lg text-center tracking-widest"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Enter the 6-digit code sent to your phone
                  </p>
                </div>

                <div className="space-y-3">
                  <Button 
                    type="submit" 
                    className="w-full" 
                    size="lg"
                    disabled={otpState.loading || otpState.code.length !== 6}
                  >
                    {otpState.loading ? (
                      <>
                        <Clock className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Verify & Sign In
                      </>
                    )}
                  </Button>

                  <div className="flex items-center justify-between text-sm">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleBackToPhone}
                      className="p-0 h-auto"
                    >
                      <ArrowLeft className="mr-1 h-3 w-3" />
                      Change Phone Number
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleResendCode}
                      disabled={otpState.resendCooldown > 0 || otpState.loading}
                      className="p-0 h-auto"
                    >
                      {otpState.resendCooldown > 0 ? (
                        `Resend in ${otpState.resendCooldown}s`
                      ) : (
                        'Resend Code'
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}

            <div className="text-center">
              <Button
                variant="link"
                onClick={() => navigate('/fancy-signin')}
                className="text-sm"
              >
                Back to Email Sign In
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SMS Consent Notice */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 text-center">
            By signing in with your phone number, you agree to receive SMS messages from Figgy. 
            Message and data rates may apply. Reply STOP to opt out.
          </p>
        </div>
      </div>
    </div>
  );
}
